// Epoch-envelope retention + the envelope write budget (2026-09-14).
//
// Every epoch bump seals the new epoch SECRET to every member — one ADK row
// per (epoch, member) — and until now the rows accumulated forever. Two
// problems with that:
//   - the service lands an envelope write as ONE DynamoDB transaction (the
//     new/changed rows + one delete per withdrawn row), capped at 100 items.
//     A rotation withdraws every old row, so an account whose
//     (epochs x members) history outgrew the cap failed AFTER the member
//     list had already chain-committed the new epochPk — which stranded the
//     new epoch's secret in one browser tab;
//   - an epoch key only matters while a record encrypted under it exists,
//     and records live 15 days server-side (events-v3 / images-v3 TTL and
//     the images bucket lifecycle). The Windows client discards un-uploaded
//     captures after 14 days too.
//
// So every minted epoch row now carries `epochSince` (ms). An epoch is
// PRUNABLE once it stopped being current more than EPOCH_RETENTION_MS ago,
// judged by the SUCCESSOR epoch's mint time — never the epoch's own (a
// year-old epoch that is still current must survive). Rows written before
// the stamp existed simply lack it; such an epoch is pruned once any LATER
// epoch's known mint time is past the window (an upper bound on when it
// stopped being current).
//
// The 60-day window is four times the record lifetime. The residual risk it
// accepts: an uploader that stays offline for more than 60 days keeps
// encrypting under the epoch key it last fetched, and whatever it uploads
// after that is unreadable (its own 14-day discard bounds the loss to its
// last two weeks). The Decryptor reports such a record as "no epoch key".
//
// Pure module (no Vue / API imports) so Node can exercise it directly.

import { canonicalize } from './shared/jcs.js'

export const EPOCH_RETENTION_MS = 60 * 24 * 60 * 60 * 1000

/** DynamoDB's TransactWriteItems cap — mirrors the service's MAX_TRANSACT_ITEMS. */
export const MAX_TRANSACT_ITEMS = 100

export const rowKey = (e) => `${e.keyEpoch}#${e.userId}`

/** epoch -> the earliest `epochSince` (ms) any of its rows carries, for epochs that carry one. */
export function epochSinceMap (envelopes) {
  const since = new Map()
  for (const e of envelopes || []) {
    if (!Number.isSafeInteger(e.epochSince) || e.epochSince <= 0) continue
    const prev = since.get(e.keyEpoch)
    if (prev === undefined || e.epochSince < prev) since.set(e.keyEpoch, e.epochSince)
  }
  return since
}

/**
 * The epochs whose rows can be withdrawn: older than the current epoch, and
 * some later epoch's known mint time is past the retention window.
 */
export function prunableEpochs (envelopes, { currentEpoch, nowMs = Date.now(), retentionMs = EPOCH_RETENTION_MS } = {}) {
  const since = epochSinceMap(envelopes)
  const epochs = [...new Set((envelopes || []).map((e) => e.keyEpoch))].sort((a, b) => a - b)
  const cutoff = nowMs - retentionMs
  const out = []
  for (const epoch of epochs) {
    if (currentEpoch !== undefined && epoch >= currentEpoch) continue
    // Upper bound on when this epoch stopped being current: the earliest
    // known mint time among the epochs after it.
    let endedBy
    for (const [other, ms] of since) {
      if (other > epoch && (endedBy === undefined || ms < endedBy)) endedBy = ms
    }
    if (endedBy !== undefined && endedBy <= cutoff) out.push(epoch)
  }
  return out
}

/** Split `envelopes` into the rows to keep and the epochs being dropped. */
export function pruneEnvelopes (envelopes, opts) {
  const drop = new Set(prunableEpochs(envelopes, opts))
  return {
    kept: (envelopes || []).filter((e) => !drop.has(e.keyEpoch)),
    prunedEpochs: [...drop]
  }
}

/**
 * Plan a replace write of `updated` over the server's `existing` rows the
 * way the service lands it: a row is written only when it is new or its
 * canonical form changed, and every existing row absent from `updated` is
 * withdrawn. Returns
 *   { puts, withdrawals, items, fits, shrinkWrites, error }
 * `shrinkWrites` are the withdraw-only arrays to send FIRST (each within the
 * cap, oldest epochs first) when the delta alone would not fit. They only
 * drop rows `updated` drops anyway, so they leave the account consistent and
 * are safe to send before the member list is chain-committed. `error` is a
 * user-facing message when no sequence of writes can land `updated`.
 */
export function planEnvelopeWrite ({ existing, updated, cap = MAX_TRANSACT_ITEMS }) {
  existing = existing || []
  const existingCanon = new Map(existing.map((e) => [rowKey(e), canonicalize(e)]))
  const updatedKeys = new Set(updated.map(rowKey))
  const puts = updated.filter((e) => existingCanon.get(rowKey(e)) !== canonicalize(e))
  const withdrawals = existing
    .filter((e) => !updatedKeys.has(rowKey(e)))
    .sort((a, b) => a.keyEpoch - b.keyEpoch || (a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0))
  const items = puts.length + withdrawals.length
  const plan = { puts, withdrawals, items, fits: items <= cap, shrinkWrites: [], error: null }
  if (plan.fits) return plan
  if (puts.length > cap) {
    plan.error = `This change needs ${puts.length} new key envelopes at once and the limit is ${cap}. `
      + 'Remove report recipients you no longer need and try again.'
    return plan
  }
  // Pre-withdraw enough rows, oldest first, that the final write fits. A
  // withdraw-only write must still leave at least one row (the service
  // refuses an empty array), so the whole set is never withdrawn up front.
  const wanted = items - cap
  const pre = Math.min(wanted, Math.max(0, existing.length - 1))
  if (puts.length + (withdrawals.length - pre) > cap) {
    plan.error = `This change needs ${items} key-envelope updates at once and the limit is ${cap}. `
      + 'Remove report recipients you no longer need and try again.'
    return plan
  }
  const dropKeys = new Set(withdrawals.slice(0, pre).map(rowKey))
  let current = existing
  let chunk = []
  const flush = () => {
    if (!chunk.length) return
    const drop = new Set(chunk)
    current = current.filter((e) => !drop.has(rowKey(e)))
    plan.shrinkWrites.push(current)
    chunk = []
  }
  for (const e of withdrawals) {
    if (!dropKeys.has(rowKey(e))) continue
    chunk.push(rowKey(e))
    if (chunk.length === cap) flush()
  }
  flush()
  return plan
}
